import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { AuthState, AuthUser, Business } from "../types/auth";
import api from "../lib/axios";

export interface LoginPayload {
  identifier: string; // email or username
  password: string;
}

interface SetEmployeePasswordPayload {
  token: string;
  password: string;
}

interface ApiResponse {
  message: string;
}


//--------------------------------------------------
// THUNKS
//--------------------------------------------------

// Async thunk for login
export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (payload: LoginPayload, { rejectWithValue }) => {
    try {
      const res = await api.post("/auth/login", payload);
      // Expecting server response in clean format: { success, message, data: { user, token } }
      const fallback = res.data?.fallbackToken;
      if (fallback) localStorage.setItem("fallbackToken", fallback);
      // console.log("Payload: ", res.data.user);
      // let authStatus;
      // attempt to verify that cookies work by calling a protected endpoint
      // try {
      //   authStatus = await api.get("/auth/check"); // make a small protected call
      //   // cookies working — remove fallback token if previously set
      //   localStorage.removeItem("fallbackToken");
      //   // cookie didn't work — store fallback token
      //   // if(authStatus?.status == 401) {
      //     //   const fallback = res.data?.fallbackToken;
      //     //   if (fallback) localStorage.setItem("fallbackToken", fallback);
      //     // }
      //   } catch (err:any)  {
      //   // cookie didn't work — store fallback token
      //    if(authStatus?.status == 401) {
      //       const fallback = res.data?.fallbackToken;
      //       if (fallback) localStorage.setItem("fallbackToken", fallback);
      //   } else {
      //       const message = err?.response?.data?.message || err.message || "Failed to fetch roles";
      //       return rejectWithValue(message);
      //   }
      // }

      return res.data;
    } catch (err: any) {
      // normalize error
      const message =
        err?.response?.data?.message || err.message || "Login failed";
      return rejectWithValue(message);
    }
  }
);

export const logout = createAsyncThunk(
  "auth/logout",
  async() => {
    await api.post("/auth/logout");
    localStorage.removeItem("fallbackToken");
  });

// Get all roles
export const getAllUserRoles = createAsyncThunk(
  "user/roles",
  async(_, { rejectWithValue}) => {
    try {
      const res = await api.get("user/roles");
      return res.data;
    } catch (err: any) {
        const message = err?.response?.data?.message || err.message || "Failed to fetch roles";
        return rejectWithValue(message);
    }
  }
)

export const requestPasswordReset = createAsyncThunk<
  ApiResponse,
  { email: string },
  { rejectValue: string }
>(
  "auth/requestPasswordReset",
  async ({ email }, { rejectWithValue }) => {
    try {
      const res = await api.post("/auth/forgot-password", { email });
      return res.data as ApiResponse;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to send reset code.");
    }
  }
);

export const resetPassword = createAsyncThunk<
  ApiResponse,
  { email: string; code: string; newPassword: string },
  { rejectValue: string }
>(
  "auth/resetPassword",
  async ({ email, code, newPassword }, { rejectWithValue }) => {
    try {
      const res = await api.post("/auth/reset-password", { email, code, newPassword });
      return res.data as ApiResponse;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to reset password.");
    }
  }
);

export const setEmployeePassword = createAsyncThunk<
  ApiResponse,                // Return type
  SetEmployeePasswordPayload, // Argument type
  { rejectValue: string }     // Rejection type
>(
  "auth/setEmployeePassword",
  async ({ token, password }, { rejectWithValue }) => {
    try {
      const res = await api.post(
        `/auth/setpassword`,
        { token, password }
      );

      return res.data as ApiResponse;
    } catch (error: any) {
      const message =
        error.response?.data?.message || "Failed to set password. Please try again.";
      return rejectWithValue(message);
    }
  }
);

const initialState: AuthState = {
  user: null,
  business: null,
  status: "idle",
  error: null,
  roles: null,
  activeRole: null,
};


//--------------------------------------------------
// SLICE
//--------------------------------------------------


const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setCredentials: (state, action: PayloadAction<{ user: AuthUser; business?: any }>) => {
      console.log("Called set credentials");
      if (!state.activeRole) {
        state.activeRole = action.payload.user.main_role;
      }

      if (action.payload.business) {
        state.business = action.payload.user.Business;
      }
    },
    updateUser: (state, action: PayloadAction<AuthUser>) => {
      state.user = action.payload;
      if (action.payload.Business) {
        state.business = action.payload.Business;
      }
    },
    updateBusiness: (state, action: PayloadAction<Business>) => {
      state.business = action.payload;
      if (state.user) {
        state.user.Business = action.payload;
      }
    },
    logoutLocal: (state) => {
      state.user = null;
      state.business = null;
      state.status = "idle";
      state.error = null;
    },
    resetAuth: () => initialState,
    updateRole: (
      state,
      action: PayloadAction<{ value: any }>
    ) => {
      console.log("Switching to: ", action.payload);
      state.activeRole = action.payload.value;
      console.log("Switched to: ", state.activeRole);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.status = "succeeded";
        const payload = action.payload;
        const user = payload?.data?.user ?? payload?.user ?? null;
        const business = payload?.data?.user.Business ?? null;
        state.user = user;
        state.activeRole = user.main_role;
        console.log("active role: ", state.activeRole);
        state.business = business;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.status = "failed";
        state.error = (action.payload as string) || action.error.message || "Login failed";
      })
      .addCase(getAllUserRoles.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getAllUserRoles.fulfilled, (state, action) => {
        state.status = "succeeded";
        const payload = action.payload;
        state.roles = payload?.data;
        state.error = null;
      })
      .addCase(getAllUserRoles.rejected, (state, action) => {
        state.status = "failed";
        state.error = (action.payload as string) || action.error.message || "Failed to fetch user roles";
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.business = null;
        state.status = "idle";
        state.error = null;
        state.stores = null;
      })
      .addCase(setEmployeePassword.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(setEmployeePassword.fulfilled, (state) => {
        state.status = "succeeded";
      })
      .addCase(setEmployeePassword.rejected, (state) => {
        state.status = "failed";
      });
  },
});

export const {
  setCredentials,
  updateUser,
  updateBusiness,
  logoutLocal,
  resetAuth,
  updateRole
} = authSlice.actions;
export const authReducer = authSlice.reducer;
